import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { IoClose } from "react-icons/io5";
import { MdCheck } from "react-icons/md";
import { useTranslation } from "react-i18next";

const PANEL_WIDTH = 240;
const PANEL_MAX_HEIGHT = 420;

const isSet = (value) =>
  value !== undefined && value !== null && String(value).trim() !== "";

const same = (a, b) =>
  String(a ?? "")
    .trim()
    .toLowerCase() ===
  String(b ?? "")
    .trim()
    .toLowerCase();

// The values of a filter that takes several (`multiple`): comma separated,
// as the server reads them ("pending,attention").
export const splitFilterValues = (value) =>
  String(value ?? "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

const sameValues = (a, b) =>
  a.length === b.length &&
  a.every((value) => b.some((other) => same(value, other)));

// What a filter's value reads as on its chip: the option's label ("Self
// Pickup", not "self pickup"); for one that takes several, a preset's name
// when the values are exactly its own, else each label ("Pending / New,
// Attention").
export const filterValueLabel = (filter, value) => {
  const labelOf = (part) =>
    filter?.options?.find((opt) => same(opt.value, part))?.label ?? part;
  if (!filter?.multiple) return labelOf(value);
  const values = splitFilterValues(value);
  const preset = (filter.presets ?? []).find((option) =>
    sameValues(option.values, values),
  );
  return preset ? preset.label : values.map(labelOf).join(", ");
};

// One filter of the pop-up. With `options`, typing narrows the list and
// "Use …" sends any other text (unless `freeText: false`, for fields the
// server matches exactly); without, the box filters as you type, like the
// header search. `multiple`: options tick on and off, several at once (the
// value lists them, comma separated), and `presets` ([{ label, values }])
// pick a set in one go.
// Also used by the phone Filters sheet (`large`: 16px input, so phones don't
// zoom in when it is focused; `showLabel={false}`: the sheet's row already
// names the filter).
export function FilterSection({
  filter,
  value,
  onChange,
  onClose,
  autoFocus,
  large = false,
  showLabel = true,
}) {
  const { t } = useTranslation("common");
  const hasOptions = Array.isArray(filter.options) && filter.options.length > 0;
  const allowsText = filter.freeText !== false;
  const [term, setTerm] = useState(hasOptions ? "" : (value ?? ""));
  const typed = term.trim();

  const shown = hasOptions
    ? filter.options.filter(
        (opt) =>
          !typed ||
          String(opt.label).toLowerCase().includes(typed.toLowerCase()),
      )
    : [];
  const isKnown = filter.options?.some(
    (opt) => same(opt.label, typed) || same(opt.value, typed),
  );
  const multiple = Boolean(filter.multiple) && hasOptions;
  const picked = multiple ? splitFilterValues(value) : [];
  const isPicked = (option) =>
    multiple ? picked.some((part) => same(part, option)) : same(value, option);
  // Typed values that aren't options: one (or, taking several, each).
  const customValues = !hasOptions
    ? []
    : (multiple ? picked : isSet(value) ? [value] : []).filter(
        (part) => !filter.options.some((opt) => same(opt.value, part)),
      );

  const pick = (next) => {
    onChange(filter.field_name, next);
    setTerm("");
  };
  // Taking several: an option ticks on or off; otherwise it's the value (or
  // none, picked again).
  const choose = (option) => {
    if (!multiple) {
      pick(isPicked(option) ? "" : option);
      return;
    }
    pick(
      (isPicked(option)
        ? picked.filter((part) => !same(part, option))
        : [...picked, String(option)]
      ).join(","),
    );
  };
  // Enter and "Use …" take a value (or add it, when several can be picked);
  // they never take one away, as before several could be picked.
  const take = (option) => {
    if (!multiple) {
      pick(option);
      return;
    }
    if (isPicked(option)) setTerm("");
    else pick([...picked, String(option)].join(","));
  };
  const presets = multiple && !typed ? (filter.presets ?? []) : [];

  const optionClass = (selected) =>
    `flex w-full items-center justify-between gap-2 rounded-md px-[8px] py-[6px] text-left text-xs transition-colors hover:bg-ui-row-hover ${
      selected ? "bg-brand-light font-medium text-brand" : "text-ui-text"
    }`;

  return (
    <div
      role="group"
      aria-label={filter.label}
      className="border-b-[1px] border-ui-border px-[10px] py-[8px] last:border-b-0"
    >
      <div className="mb-[6px] flex items-center justify-between gap-2">
        {showLabel ? (
          <span className="text-[10px] font-semibold uppercase tracking-wide text-ui-text-subtle">
            {filter.label}
          </span>
        ) : (
          <span />
        )}
        {isSet(value) && (
          <button
            type="button"
            onClick={() => {
              setTerm("");
              onChange(filter.field_name, "");
            }}
            className="text-[11px] font-medium text-brand hover:underline"
          >
            {t("actions.clear")}
          </button>
        )}
      </div>
      <input
        type="text"
        autoFocus={autoFocus}
        value={term}
        aria-label={t("table.filterBy", { name: filter.label })}
        placeholder={
          !hasOptions
            ? t("table.searchIn", { name: filter.label })
            : allowsText
              ? t("table.searchOrType")
              : t("table.searchShort")
        }
        onChange={(e) => {
          setTerm(e.target.value);
          if (!hasOptions) onChange(filter.field_name, e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") onClose();
          if (e.key === "Enter" && hasOptions && typed) {
            e.preventDefault();
            if (shown.length === 1) take(shown[0].value);
            else if (allowsText) take(typed);
          }
        }}
        className={`w-full rounded-md border-[1px] border-ui-border bg-ui-surface px-[8px] py-[5px] text-ui-text outline-none transition-colors focus:border-brand ${
          large ? "text-[16px]" : "text-xs"
        }`}
      />
      {hasOptions && (
        <div className="mt-[4px] grid gap-[1px]">
          {presets.map((preset) => {
            const selected = sameValues(preset.values, picked);
            return (
              <button
                key={`preset:${preset.label}`}
                type="button"
                aria-pressed={selected}
                title={preset.hint}
                onClick={() => pick(selected ? "" : preset.values.join(","))}
                className={`${optionClass(selected)} border-b-[1px] border-ui-border`}
              >
                <span className="truncate">{preset.label}</span>
                {selected && <MdCheck className="h-3.5 w-3.5 shrink-0" />}
              </button>
            );
          })}
          {!typed &&
            customValues.map((custom) => (
              <button
                key={`custom:${custom}`}
                type="button"
                aria-pressed="true"
                onClick={() => choose(custom)}
                className={optionClass(true)}
              >
                <span className="truncate">“{custom}”</span>
                <MdCheck className="h-3.5 w-3.5 shrink-0" />
              </button>
            ))}
          {shown.map((opt) => {
            const selected = isPicked(opt.value);
            return (
              <button
                key={String(opt.value)}
                type="button"
                aria-pressed={selected}
                onClick={() => choose(opt.value)}
                className={optionClass(selected)}
              >
                <span className="truncate">{opt.label}</span>
                {opt.count != null ? (
                  // Optional per-value count (e.g. orders of that type).
                  <span className="ml-auto flex shrink-0 items-center gap-[6px]">
                    <span className="text-[10.5px] font-normal tabular-nums text-ui-text-subtle">
                      {opt.count}
                    </span>
                    {selected && <MdCheck className="h-3.5 w-3.5 shrink-0" />}
                  </span>
                ) : (
                  selected && <MdCheck className="h-3.5 w-3.5 shrink-0" />
                )}
              </button>
            );
          })}
          {typed && !isKnown && allowsText && (
            <button
              type="button"
              onClick={() => take(typed)}
              className={optionClass(false)}
            >
              <span className="truncate">
                {t("table.useValue", { value: typed })}
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Filter pop-up of a column heading, for columns that list `headerFilters`
 * ({ field_name, label, options? [{ label, value, count? }], freeText?,
 * multiple?, presets? [{ label, values, hint? }] }). Each filter sends
 * `&field_name=value` (several values comma separated),
 * the same request as a header search or toolbar dropdown on that field.
 */
export default function HeaderFilters({
  label,
  filters,
  filterValues,
  anchorEl,
  onChange,
  onClose,
}) {
  const panelRef = useRef(null);
  const { t } = useTranslation("common");
  const [rect, setRect] = useState(() => anchorEl.getBoundingClientRect());

  // Stay attached to the heading when the page scrolls or resizes (on phones
  // the keyboard does both), as the toolbar dropdowns do.
  useEffect(() => {
    let frame = null;
    const reposition = (event) => {
      if (
        event?.target instanceof Node &&
        panelRef.current?.contains(event.target)
      ) {
        return;
      }
      if (frame !== null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        setRect(anchorEl.getBoundingClientRect());
      });
    };

    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [anchorEl]);

  const viewport = window.visualViewport;
  const visibleBottom = viewport
    ? viewport.offsetTop + viewport.height
    : window.innerHeight;
  const top = Math.max(8, rect.bottom + 4);

  return createPortal(
    <>
      <div className="fixed inset-0 z-[58]" onClick={onClose} />
      <div
        ref={panelRef}
        role="dialog"
        aria-label={t("table.filtersFor", { name: label })}
        style={{
          top,
          left: Math.max(
            8,
            Math.min(rect.left, window.innerWidth - PANEL_WIDTH - 8),
          ),
          width: PANEL_WIDTH,
          maxHeight: Math.max(
            120,
            Math.min(PANEL_MAX_HEIGHT, visibleBottom - top - 8),
          ),
        }}
        className="thin__scrollbar fixed z-[59] flex flex-col overflow-y-auto rounded-lg border-[1px] border-ui-border bg-ui-surface-raised shadow-ui-md"
      >
        <div className="flex items-center justify-between border-b-[1px] border-ui-border px-[10px] py-[6px]">
          <span className="text-xs font-semibold text-ui-text">{label}</span>
          <button
            type="button"
            aria-label={t("table.closeFilters")}
            onClick={onClose}
            className="rounded p-[2px] text-ui-text-subtle hover:text-ui-text"
          >
            <IoClose className="h-3.5 w-3.5" />
          </button>
        </div>
        {filters.map((filter, index) => (
          <FilterSection
            key={filter.field_name}
            filter={filter}
            value={filterValues?.[filter.field_name]}
            onChange={onChange}
            onClose={onClose}
            autoFocus={index === 0}
          />
        ))}
      </div>
    </>,
    document.body,
  );
}
